/**
 * MultiplayerManager.js
 * Central orchestrator for the experimental WebRTC multiplayer system in FRONTROOMS.
 * Ties signaling, room presence, name tags, and voice spatialization together.
 */
import { RTCSignalingManager } from './RTCSignalingManager.js';
import { RoomPresenceSystem } from './RoomPresenceSystem.js';
import { VoiceManager } from './VoiceManager.js';
import { NameTagSystem } from './NameTagSystem.js';

export class MultiplayerManager {
  constructor() {
    this.signaling = new RTCSignalingManager();
    this.presence = new RoomPresenceSystem();
    this.voice = new VoiceManager();
    this.nametags = new NameTagSystem();

    this.worldManager = null;
    this.THREE = null;
    this.localPeerId = 'explorer_' + Math.random().toString(36).substring(2, 9);
    
    // Configuration states
    this.config = {
      enabled: false,
      nickname: localStorage.getItem('multiplayer_nickname') || ('EXPLORER_' + Math.floor(1000 + Math.random() * 9000)),
      serverUrl: localStorage.getItem('multiplayer_server_url') || 'wss://frontrooms-signaling.glitch.me',
      voiceEnabled: localStorage.getItem('multiplayer_voice_enabled') === 'true',
      maxTagDistance: parseFloat(localStorage.getItem('multiplayer_max_distance')) || 15
    };

    this.peers = new Map(); // peerId -> { connection, dataChannel, nickname, position, micMuted }
    this.lastSyncTime = 0;
    this.syncInterval = 50; // Sync position every 50ms for smooth tracking
  }

  /**
   * Initializes sub-modules with game resources.
   * @param {WorldManager} worldManager - Main world controller
   */
  init(worldManager) {
    this.worldManager = worldManager;
    this.THREE = worldManager.textures.THREE; // Retrieve THREE instance

    // Initialize systems
    this.nametags.init(this.THREE);
    this.nametags.maxDistance = this.config.maxTagDistance;

    // Connect RoomPresence callback
    this.presence.onRoomChangeCallback = (rx, rz) => {
      this.handleRoomTransition(rx, rz);
    };

    // Connect Signaling callbacks
    this.signaling.onConnectCallback = () => {
      console.log('[MultiplayerManager] Connected to signaling channel.');
    };

    this.signaling.onDisconnectCallback = () => {
      console.log('[MultiplayerManager] Disconnected from signaling channel.');
      this.cleanupAllPeers();
    };

    this.signaling.onPeerJoinedCallback = (peerId, remoteNickname, initiate) => {
      this.handlePeerJoined(peerId, remoteNickname, initiate);
    };

    this.signaling.onPeerLeftCallback = (peerId) => {
      this.handlePeerLeft(peerId);
    };

    this.signaling.onSignalCallback = (senderId, data) => {
      this.handleSignalingPayload(senderId, data);
    };

    this.signaling.onErrorCallback = (err) => {
      console.warn('[MultiplayerManager] Signaling system error:', err);
    };

    // Auto-save initial configs
    localStorage.setItem('multiplayer_nickname', this.config.nickname);
    localStorage.setItem('multiplayer_server_url', this.config.serverUrl);
  }

  /**
   * Starts the multiplayer network thread.
   */
  async start() {
    if (!this.worldManager) return;
    
    console.log('[MultiplayerManager] Starting multiplayer subsystem...');
    this.config.enabled = true;

    // A. Start Voice Manager spatial context
    await this.voice.init(this.THREE);
    if (this.config.voiceEnabled) {
      try {
        await this.voice.startMicrophone();
        this.voice.setMute(false); // Unmute microphone if voice is toggled
      } catch (err) {
        console.warn('[MultiplayerManager] Failed to start microphone on startup:', err);
      }
    } else {
      this.voice.setMute(true);
    }

    // B. Start room presence detector
    this.presence.reset();
    
    // C. Connect socket signalling channel
    this.signaling.connect(this.config.serverUrl, this.localPeerId, this.config.nickname);
  }

  /**
   * Stops the multiplayer network thread and cleans memory.
   */
  stop() {
    console.log('[MultiplayerManager] Stopping multiplayer subsystem...');
    this.config.enabled = false;

    // Disconnect socket & peers
    this.signaling.close();
    this.cleanupAllPeers();

    // Close spatial WebAudio and camera listeners
    this.voice.destroy();
    this.nametags.destroy();
    this.presence.reset();
  }

  /**
   * Handles room transition event.
   */
  handleRoomTransition(roomX, roomZ) {
    if (!this.config.enabled) return;

    // Disconnect old peer connections since we only share presence in the exact same room
    console.log(`[MultiplayerManager] Transitioned to room: (${roomX}, ${roomZ}). Purging old peers.`);
    this.cleanupAllPeers();

    // Connect to the new room signaling pool
    this.signaling.joinRoom(roomX, roomZ);
  }

  /**
   * Creates an RTCPeerConnection for a remote explorer.
   */
  async handlePeerJoined(peerId, remoteNickname, initiate) {
    if (this.peers.has(peerId)) return;

    console.log(`[MultiplayerManager] Setting up WebRTC link with peer: ${remoteNickname} (${peerId})`);

    // Standard public STUN configuration
    const pcConfig = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    };

    const pc = new RTCPeerConnection(pcConfig);
    const peerState = {
      connection: pc,
      dataChannel: null,
      nickname: remoteNickname,
      position: new this.THREE.Vector3(),
      micMuted: true
    };

    this.peers.set(peerId, peerState);

    // Bind ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.signaling.sendSignal(peerId, { candidate: event.candidate });
      }
    };

    // Bind incoming media streams (Voice tracks)
    pc.ontrack = (event) => {
      if (event.streams && event.streams[0]) {
        console.log(`[MultiplayerManager] Received remote audio stream from peer: ${peerId}`);
        this.voice.addPeerStream(peerId, event.streams[0]);
      }
    };

    // Add local mic track if capture is active
    if (this.config.voiceEnabled && this.voice.localStream) {
      this.voice.localStream.getTracks().forEach(track => {
        pc.addTrack(track, this.voice.localStream);
      });
    }

    // Set up tags in UI
    this.nametags.addTag(peerId, remoteNickname);

    if (initiate) {
      // 1. We are the initiator: create position sync data channel
      const channel = pc.createDataChannel('sync-channel');
      this.setupDataChannel(peerId, channel);

      // 2. Compile SDP offer
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        this.signaling.sendSignal(peerId, { offer: offer });
      } catch (err) {
        console.error('[MultiplayerManager] Error creating RTC offer:', err);
      }
    } else {
      // We are the recipient: wait for DataChannel from initiator
      pc.ondatachannel = (event) => {
        this.setupDataChannel(peerId, event.channel);
      };
    }
  }

  /**
   * Sets up communication channels for coordinate syncs.
   */
  setupDataChannel(peerId, channel) {
    const peer = this.peers.get(peerId);
    if (!peer) return;

    peer.dataChannel = channel;

    channel.onopen = () => {
      console.log(`[MultiplayerManager] DataChannel opened with: ${peer.nickname}`);
    };

    channel.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handlePeerMessage(peerId, data);
      } catch (e) {
        console.warn('[MultiplayerManager] Error parsing Peer message:', e);
      }
    };

    channel.onclose = () => {
      console.log(`[MultiplayerManager] DataChannel closed with: ${peer.nickname}`);
    };
  }

  /**
   * Directs received synchronization packets.
   */
  handlePeerMessage(peerId, data) {
    const peer = this.peers.get(peerId);
    if (!peer) return;

    if (data.type === 'sync') {
      // Update absolute coordinates in ThreeJS
      peer.position.set(data.x, data.y, data.z);
      peer.micMuted = data.micMuted;

      // Update HUD/Tag indicators
      this.nametags.updateMicState(peerId, data.micMuted);
    }
  }

  /**
   * Processes incoming WebRTC signaling states.
   */
  async handleSignalingPayload(senderId, data) {
    let peer = this.peers.get(senderId);

    // If we haven't registered this peer, do it now (recipient path)
    if (!peer) {
      await this.handlePeerJoined(senderId, 'Explorer', false);
      peer = this.peers.get(senderId);
    }

    const pc = peer.connection;

    try {
      if (data.offer) {
        await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.signaling.sendSignal(senderId, { answer: answer });
      } else if (data.answer) {
        await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
      } else if (data.candidate) {
        await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
      }
    } catch (error) {
      console.error('[MultiplayerManager] Error negotiating WebRTC signal:', error);
    }
  }

  /**
   * Disconnects a remote explorer.
   */
  handlePeerLeft(peerId) {
    const peer = this.peers.get(peerId);
    if (!peer) return;

    console.log(`[MultiplayerManager] Explorer left: ${peer.nickname}`);
    
    // Close connections
    try {
      if (peer.dataChannel) peer.dataChannel.close();
      if (peer.connection) peer.connection.close();
    } catch (e) {}

    // Cleanup assets
    this.voice.removePeerStream(peerId);
    this.nametags.removeTag(peerId);

    this.peers.delete(peerId);
  }

  /**
   * Cleans all peer configurations.
   */
  cleanupAllPeers() {
    for (const peerId of this.peers.keys()) {
      this.handlePeerLeft(peerId);
    }
    this.peers.clear();
  }

  /**
   * Master frame update called in WorldManager animate() loop.
   * Tracks local coordinates, relays positions via WebRTC and adjusts audio nodes.
   * @param {number} delta - Elapsed frame delta
   */
  update(delta) {
    if (!this.config.enabled || !this.worldManager || !this.worldManager.player) return;

    const player = this.worldManager.player;

    // 1. Poll presence to detect room changes
    this.presence.update(player.position);

    // 2. Broadcast local player absolute position to active room peers
    const now = performance.now();
    if (now - this.lastSyncTime > this.syncInterval) {
      this.lastSyncTime = now;
      this.broadcastLocalState();
    }

    // 3. Compile map of active remote peer absolute positions for NameTags and WebAudio listener updates
    const positionsMap = new Map();
    for (const [peerId, peer] of this.peers.entries()) {
      positionsMap.set(peerId, peer.position);
    }

    // 4. Update name tag screen projections and oclusión physics
    this.nametags.update(
      this.worldManager.camera,
      this.worldManager.scene,
      positionsMap,
      this.worldManager.ghostMesh
    );

    // 5. Update WebAudio spatial orientations
    this.voice.update(this.worldManager.camera, positionsMap);
  }

  /**
   * Compiles local player position/yaw/mic states and sends them to room peers.
   */
  broadcastLocalState() {
    if (!this.worldManager || !this.worldManager.player) return;

    const player = this.worldManager.player;
    
    const payload = JSON.stringify({
      type: 'sync',
      x: player.position.x,
      y: player.position.y,
      z: player.position.z,
      micMuted: !this.config.voiceEnabled
    });

    for (const peer of this.peers.values()) {
      if (peer.dataChannel && peer.dataChannel.readyState === 'open') {
        try {
          peer.dataChannel.send(payload);
        } catch (e) {
          console.warn('[MultiplayerManager] Position transmission failed:', e);
        }
      }
    }
  }

  /**
   * Programmatic runtime modifications of settings options.
   */
  setNickname(val) {
    this.config.nickname = val || 'EXPLORER';
    localStorage.setItem('multiplayer_nickname', this.config.nickname);
  }

  setServerUrl(val) {
    this.config.serverUrl = val || 'wss://frontrooms-signaling.glitch.me';
    localStorage.setItem('multiplayer_server_url', this.config.serverUrl);
  }

  setMaxTagDistance(val) {
    this.config.maxTagDistance = val;
    this.nametags.maxDistance = val;
    localStorage.setItem('multiplayer_max_distance', val.toString());
  }

  async setVoiceEnabled(enabled) {
    this.config.voiceEnabled = enabled;
    localStorage.setItem('multiplayer_voice_enabled', enabled.toString());

    if (!this.config.enabled) return;

    if (enabled) {
      try {
        await this.voice.startMicrophone();
        this.voice.setMute(false);

        // Re-negotiate audio tracks on all active connections
        for (const [peerId, peer] of this.peers.entries()) {
          const pc = peer.connection;
          // Add local track
          this.voice.localStream.getTracks().forEach(track => {
            pc.addTrack(track, this.voice.localStream);
          });
          // Re-offer SDP
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          this.signaling.sendSignal(peerId, { offer: offer });
        }
      } catch (err) {
        console.warn('[MultiplayerManager] Failed to start microphone track renegotiation:', err);
      }
    } else {
      this.voice.setMute(true);
      this.voice.stopMicrophone();
      
      // Remove local audio tracks from all peer connections
      for (const peer of this.peers.values()) {
        const pc = peer.connection;
        pc.getSenders().forEach(sender => {
          if (sender.track && sender.track.kind === 'audio') {
            pc.removeTrack(sender);
          }
        });
      }
    }
  }
}

// Instantiate master global singletons for direct import
export const multiplayer = new MultiplayerManager();
